import type { CSSProperties } from 'react';

/**
 * Spec 234 §5 / task 4 — the gauge spec 231's stat cell was left without.
 *
 * A semicircle, centre (60,60), radius 48, in a 120×68 viewBox: a 10px track
 * in `--hub-gauge-track`, a 2px arc in `--hub-gauge-arc` to the fraction, 3px
 * end dots, a 4px needle ring. Monochrome by construction — the only
 * variation is `attention`, which mirrors `Stat`'s own `attention` prop and
 * swaps the arc/needle for the danger colour, never a second decorative hue.
 *
 * Pure SVG geometry, no data/series logic. `Stat` decides whether to mount
 * one at all — a cell with no fraction to show renders no `<Gauge>`, so it
 * never grows an empty dial.
 */
const CENTER_X = 60;
const CENTER_Y = 60;
const RADIUS = 48;

/** A point on the gauge's semicircle. `t` runs 0 (left end) -> 1 (right end). */
function pointAt(t: number): { x: number; y: number } {
  const angle = Math.PI - t * Math.PI; // 180deg (left) sweeping down to 0deg (right)
  return {
    x: CENTER_X + RADIUS * Math.cos(angle),
    y: CENTER_Y - RADIUS * Math.sin(angle),
  };
}

export interface GaugeProps {
  /** 0–1 fraction of the arc to fill. Clamped to [0, 1]. */
  fraction: number;
  /** Danger colour instead of ink — only when the figure genuinely asks for
   *  action, same rule as `Stat`'s `attention`. */
  attention?: boolean;
  className?: string;
  /** Spec 251: a signed scale (sentiment, −1..+1). `fraction` is then the
   *  POSITION on the arc (0.5 = zero), and the arc fills from the top centre
   *  toward the value — left for negative, right for positive — with a dot
   *  marking zero. The ends are labelled by the stat cell, not in the SVG,
   *  so the labels keep a real 12px at every width. */
  diverging?: boolean;
}

export function Gauge({ fraction, attention, className, diverging }: GaugeProps) {
  const f = Number.isFinite(fraction) ? Math.min(1, Math.max(0, fraction)) : 0;
  const start = pointAt(0);
  const end = pointAt(1);
  const valueEnd = pointAt(f);
  const arcColor = attention ? 'var(--hub-color-danger)' : 'var(--hub-gauge-arc)';

  const trackPath = `M ${start.x} ${start.y} A ${RADIUS} ${RADIUS} 0 0 1 ${end.x} ${end.y}`;
  // Sweeping clockwise from the left end.
  const zero = pointAt(0.5);
  const valuePath = diverging
    ? Math.abs(f - 0.5) > 0.001
      // From the top centre; at most a quarter turn, so never the large arc.
      // Clockwise toward the right end for a positive value, anticlockwise left.
      ? `M ${zero.x} ${zero.y} A ${RADIUS} ${RADIUS} 0 0 ${f > 0.5 ? 1 : 0} ${valueEnd.x} ${valueEnd.y}`
      : null
    : f > 0
      // Spec 252: the large-arc flag is ALWAYS 0. The whole dial is a half
      // circle, so no value arc ever exceeds 180°. It used to be set past 50%,
      // which asked SVG for the OTHER arc between the same two points — the
      // long way round, under the dial, clipped by the viewBox — so a 60%
      // gauge showed two stray ticks and no arc.
      ? `M ${start.x} ${start.y} A ${RADIUS} ${RADIUS} 0 0 1 ${valueEnd.x} ${valueEnd.y}`
      : null;

  // Spec 256: the arc's own length (radius × swept angle) for the draw-in dash.
  // Computed, not `pathLength`, so the path's geometry APIs stay in user units.
  const valueLength = (diverging ? Math.abs(f - 0.5) : f) * Math.PI * RADIUS;

  return (
    <svg
      viewBox="0 0 120 68"
      // Spec 256: the value arc draws once on mount (600ms, eased); the CSS
      // drops the animation under prefers-reduced-motion.
      className={`hub-gauge--draw w-full max-w-[120px]${className ? ` ${className}` : ''}`}
      role="img"
      aria-hidden="true"
      data-gauge-diverging={diverging || undefined}
    >
      <path d={trackPath} fill="none" stroke="var(--hub-gauge-track)" strokeWidth={10} strokeLinecap="round" />
      {valuePath && (
        <path
          className="hub-gauge__value"
          d={valuePath}
          style={{ '--hub-gauge-len': `${valueLength}px` } as CSSProperties}
          fill="none"
          stroke={arcColor}
          strokeWidth={2}
          strokeLinecap="round"
        />
      )}
      {/* 3px end dots mark the fixed 0%/100% ends of the scale. */}
      <circle cx={start.x} cy={start.y} r={1.5} fill="var(--hub-gauge-arc)" />
      <circle cx={end.x} cy={end.y} r={1.5} fill="var(--hub-gauge-arc)" />
      {/* A signed scale marks its zero at the top. */}
      {diverging && <circle cx={zero.x} cy={zero.y} r={1.5} fill="var(--hub-gauge-arc)" data-gauge-zero="" />}
      {/* 4px needle ring marks the current value. */}
      <circle className="hub-gauge__needle" cx={valueEnd.x} cy={valueEnd.y} r={1} fill="var(--hub-color-surface)" stroke={arcColor} strokeWidth={2} />
    </svg>
  );
}
